import React, { useEffect, useState } from 'react';
import { DATABASE_MONITORING_METRIC } from 'constant';
import {
  createFetchAction, computePercentageData, dataValueSortFunction, getInstanceName, computeUsedData, sortByRole, sortByCategory,
} from 'utils/metrics';
import { _t } from 'utils';
import ChartGenerator from './ChartGenerator';
import { ChartPercentage } from './charts/ChartPercentage';
import { DateBasedChart } from './charts/DateBasedChart';

const DB_MET = DATABASE_MONITORING_METRIC;

const Grapher = ({
  clusterId: uuid, timeRangeSettings, refresh, database,
}) => {
  const [data, setData] = useState({});

  const instanceCount = data[DB_MET.IONOS_DBAAS_POSTGRES_INSTANCE_COUNT]?.[0];

  const metrics = [
    {
      label: _t('DATABASES.MONITORING.METRICS.RAM_USAGE'),
      charts: [
        {
          component: ChartPercentage,
          bytes: true,
          data: computePercentageData(data, DB_MET.IONOS_DBAAS_POSTGRES_MEMORY_TOTAL_BYTES, DB_MET.IONOS_DBAAS_POSTGRES_MEMORY_AVAILABLE_BYTES),
          icon: 'memory',
          label: _t('DATABASES.MONITORING.METRICS.RAM_USAGE'),
        },
      ],
      sideBySide: false,
      style: { width: '100%' },
      metricKeys: [DB_MET.IONOS_DBAAS_POSTGRES_MEMORY_AVAILABLE_BYTES, DB_MET.IONOS_DBAAS_POSTGRES_MEMORY_TOTAL_BYTES],
    },
    {
      label: _t('DATABASES.MONITORING.METRICS.STORAGE_USAGE'),
      charts: [
        {
          component: ChartPercentage,
          bytes: true,
          data: computePercentageData(data, DB_MET.IONOS_DBAAS_POSTGRES_STORAGE_TOTAL_BYTES, DB_MET.IONOS_DBAAS_POSTGRES_STORAGE_AVAILABLE_BYTES),
          icon: 'hdd',
          label: _t('DATABASES.MONITORING.METRICS.STORAGE_USAGE'),
        },
      ],
      sideBySide: false,
      style: { width: '100%' },
      metricKeys: [DB_MET.IONOS_DBAAS_POSTGRES_STORAGE_AVAILABLE_BYTES, DB_MET.IONOS_DBAAS_POSTGRES_STORAGE_TOTAL_BYTES],
    },
    {
      label: _t('DATABASES.MONITORING.METRICS.CPU_RATE5'),
      charts: [
        {
          component: ChartPercentage,
          percentage: true,
          data: computePercentageData(data, null, DB_MET.IONOS_DBAAS_POSTGRES_CPU_RATE5),
          icon: 'tachometer-alt',
          label: _t('DATABASES.MONITORING.METRICS.CPU_RATE5'),
        },
      ],
      sideBySide: false,
      style: { width: '100%' },
      metricKeys: [DB_MET.IONOS_DBAAS_POSTGRES_CPU_RATE5],
    },
    {
      label: _t('DATABASES.MONITORING.METRICS.AVAILABLE_MEMORY'),
      metricKeys: DATABASE_MONITORING_METRIC.IONOS_DBAAS_POSTGRES_MEMORY_AVAILABLE_BYTES,
      charts: [{
        component: DateBasedChart,
        bytes: true,
        legendWidth: 200,
        height: 300,
        filterable: true,
        legendLayout: 'vertical',
        maxYAxisValue: database.ram * 1024 * 1024,
        data: computeUsedData(data, DB_MET.IONOS_DBAAS_POSTGRES_MEMORY_TOTAL_BYTES, DB_MET.IONOS_DBAAS_POSTGRES_MEMORY_AVAILABLE_BYTES),
        // data: data[DB_MET.IONOS_DBAAS_POSTGRES_MEMORY_AVAILABLE_BYTES]?.filter((item) => ['master', 'replica'].includes(item.role))
        //   .map((item) => ({ category: getInstanceName(item), dataValues: item.values })),
      }],
      style: { height: '300px' },
    },
    {
      label: _t('DATABASES.MONITORING.METRICS.AVAILABLE_STORAGE'),
      metricKeys: DATABASE_MONITORING_METRIC.IONOS_DBAAS_POSTGRES_STORAGE_AVAILABLE_BYTES,
      charts: [{
        component: DateBasedChart,
        bytes: true,
        legendWidth: 200,
        height: 300,
        filterable: true,
        legendLayout: 'vertical',
        maxYAxisValue: database.storageSize * 1024 * 1024,
        data: computeUsedData(data, DB_MET.IONOS_DBAAS_POSTGRES_STORAGE_TOTAL_BYTES, DB_MET.IONOS_DBAAS_POSTGRES_STORAGE_AVAILABLE_BYTES),
        // data: data[DB_MET.IONOS_DBAAS_POSTGRES_STORAGE_AVAILABLE_BYTES]?.map((item) => ({ category: getInstanceName(item), dataValues: item.values })),
      }],
      style: { height: '300px' },
    },
    {
      label: _t('DATABASES.MONITORING.METRICS.CPU_CAPACITY'),
      metricKeys: DATABASE_MONITORING_METRIC.IONOS_DBAAS_POSTGRES_CPU_RATE5,
      avg: true,
      charts: [{
        component: DateBasedChart,
        percentage: true,
        legendWidth: 200,
        height: 300,
        filterable: true,
        legendLayout: 'vertical',
        maxYAxisValue: 100,
        data: data[DB_MET.IONOS_DBAAS_POSTGRES_CPU_RATE5]?.sort(sortByRole).map((item) => ({
          category: getInstanceName(item),
          dataValues: item.values.map((valueItem) => ({ ...valueItem, value: valueItem.value * 100 })),
        })),
      }],
      style: { height: '300px' },
    },
    {
      label: _t('DATABASES.MONITORING.METRICS.DISK_IO'),
      metricKeys: DATABASE_MONITORING_METRIC.IONOS_DBAAS_POSTGRES_DISK_IO,
      charts: [{
        component: DateBasedChart,
        minMaxYAxisValue: 1,
        legendWidth: 200,
        height: 300,
        filterable: true,
        legendLayout: 'vertical',
        data: data[DB_MET.IONOS_DBAAS_POSTGRES_DISK_IO]?.sort(sortByRole).map((item) => ({ category: getInstanceName(item), dataValues: item.values })),
      }],
      style: { height: '300px' },
    },
    {
      label: _t('DATABASES.MONITORING.METRICS.CONNECTIONS'),
      style: { height: '300px' },
      metricKeys: [DATABASE_MONITORING_METRIC.IONOS_DBAAS_POSTGRES_CONNECTIONS_COUNT],
      charts: [{
        component: DateBasedChart,
        legendWidth: 200,
        height: 300,
        filterable: true,
        legendLayout: 'vertical',
        data: data[DB_MET.IONOS_DBAAS_POSTGRES_CONNECTIONS_COUNT]?.filter((item) => ['active', 'idle', 'disabled'].includes(item.state))
          .map((item) => ({ category: `${getInstanceName(item)} (${item.state})`, dataValues: item.values })).sort(dataValueSortFunction)
          .sort(sortByCategory),
      }],
    },
    // { // I dont see advantage in this metric
    //   metricKeys: DATABASE_MONITORING_METRIC.IONOS_DBAAS_POSTGRES_ROLE,
    // },
    {
      label: _t('DATABASES.MONITORING.METRICS.2_MIN_TRANSACTIONS'),
      metricKeys: DATABASE_MONITORING_METRIC.IONOS_DBAAS_POSTGRES_TRANSACTIONS,
      charts: [{
        component: DateBasedChart,
        legendWidth: 200,
        height: 300,
        filterable: true,
        legendLayout: 'vertical',
        data: data[DB_MET.IONOS_DBAAS_POSTGRES_TRANSACTIONS]?.filter(
          (item) => item.datname && !['template1', 'template0'].includes(item.datname),
        ).map((item) => ({ category: getInstanceName(item), dataValues: item.values })),
      }],
      style: { height: '300px' },
    },
    {
      label: _t('DATABASES.MONITORING.METRICS.LOAD_5'),
      metricKeys: DB_MET.IONOS_DBAAS_POSTGRES_LOAD5,
      charts: [{
        component: DateBasedChart,
        minMaxYAxisValue: 1,
        legendWidth: 200,
        height: 300,
        filterable: true,
        legendLayout: 'vertical',
        data: data[DB_MET.IONOS_DBAAS_POSTGRES_LOAD5]?.map((item) => ({ category: getInstanceName(item), dataValues: item.values }))
          .sort(sortByCategory),
      }],
      style: { height: '300px' },
    },
    {
      label: _t('DATABASES.MONITORING.METRICS.INDEX_SCAN_NUMBER'),
      metricKeys: DB_MET.IONOS_DBAAS_POSTGRES_USER_TABLES_IDX_SCAN,
      // metricFields: ['role', 'relname', 'schemaname'],
      rate: '5m',
      charts: [{
        component: DateBasedChart,
        minMaxYAxisValue: 1,
        legendWidth: 200,
        height: 300,
        filterable: true,
        legendLayout: 'vertical',
        data: data[DB_MET.IONOS_DBAAS_POSTGRES_USER_TABLES_IDX_SCAN]?.filter((item) => item.role === 'master').map((item, idx) => (
          { category: `${item.relname}__${idx}`, dataValues: item.values })).sort(dataValueSortFunction).sort(sortByCategory),
      }],
      style: { height: '300px' },
    },
    {
      label: _t('DATABASES.MONITORING.METRICS.INDEX_SEQUENTIAL_NUMBER'),
      metricKeys: [DB_MET.IONOS_DBAAS_POSTGRES_USER_TABLES_SEQ_SCAN],
      rate: '5m',
      charts: [{
        component: DateBasedChart,
        minMaxYAxisValue: 1,
        legendWidth: 200,
        height: 300,
        filterable: true,
        legendLayout: 'vertical',
        data: data[DB_MET.IONOS_DBAAS_POSTGRES_USER_TABLES_SEQ_SCAN]?.filter((item) => item.role === 'master').map((item, idx) => (
          { category: `${item.relname}__${idx}`, dataValues: item.values }))
          .sort(dataValueSortFunction).sort(sortByCategory),
      }],
      style: { height: '300px' },
    },
    {
      label: _t('DATABASES.MONITORING.METRICS.CURRENT_INSTANCES_COUNT'),
      metricKeys: [DB_MET.IONOS_DBAAS_POSTGRES_INSTANCE_COUNT],
      charts: [{
        component: DateBasedChart,
        legendWidth: 200,
        hideLegend: true,
        data: instanceCount && [{ category: instanceCount.role, dataValues: instanceCount.values }],
      }],
      style: { height: '300px', width: 'calc(100% - 250px)' },
    },
  ];

  const loadMetrics = async () => {
    const metricsObject = {};
    const metricsIdentifier = metrics.reduce((acc, item) => {
      if (item.metricKeys) {
        acc = [...acc, item.metricKeys];
      }

      [item.metricKeys].flat().forEach((key) => {
        metricsObject[key] = {
          metric: key,
          rate: item.rate,
          avg: item.avg,
          metricFields: item.metricFields,
        };
      });
      return acc;
    }, []).flat();

    const uniqueMetrics = Array.from(new Set(metricsIdentifier));

    const result = (await Promise.all(uniqueMetrics
      .map((uniqueMetric) => createFetchAction(metricsObject[uniqueMetric], { uuid })(timeRangeSettings))))
      .reduce((acc, item) => ({ ...acc, ...item }), {});
    setData(result);
  };

  useEffect(() => {
    loadMetrics();
    // eslint-disable-next-line
  }, [refresh]);

  return (
    <>
      {metrics.map((metric, index) => {
        if (metric.metrics) {
          return (
            <div className="metricsArea" key={index} style={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
              {metric.metrics.map((innerMetric, innerIndex) => (
                <ChartGenerator
                  key={`${index}_${innerIndex}`}
                  {...innerMetric}
                />
              ))}
            </div>
          );
        }

        return (
          <ChartGenerator
            className="metricsArea"
            key={index}
            {...metric}
          />
        );
      })}
    </>
  );
};

export default React.memo(Grapher);
